import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import { notifySetupComplete } from '../../utils/notifyClientPortal';

const ClientProfileContext = createContext();

export const useClientProfile = () => {
  const context = useContext(ClientProfileContext);
  if (!context) {
    throw new Error('useClientProfile must be used within a ClientProfileProvider');
  }
  return context;
};

const emptyProfile = {
  first_name: '',
  last_name: '',
  job_title: '',
  company_name: '',
  email: '',
  mobile_phone: '',
  secondary_phone: '',
  brand_supplier_name: '',
  address_line_1: '',
  address_line_2: '',
  address_line_3: '',
  town_city: '',
  postcode: '',
  country: '',
  ship_from_same_as_address: true,
  ship_from_address_line_1: '',
  ship_from_address_line_2: '',
  ship_from_address_line_3: '',
  ship_from_town_city: '',
  ship_from_postcode: '',
  ship_from_country: '',
};

export const ClientProfileProvider = ({ children }) => {
  const { user, isVendr } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    if (!user || !isVendr) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('client_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        setError(fetchError.message);
        setProfile(null);
        return;
      }
      setProfile(data);
    } catch (err) {
      setError(err.message);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user, isVendr]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const saveProfile = async (values) => {
    if (!user || !isVendr) return { success: false, error: 'Not authorized' };
    setError(null);
    try {
      const payload = {
        ...values,
        updated_at: new Date().toISOString(),
        setup_completed_at: new Date().toISOString(),
      };

      if (profile?.id) {
        const { data, error: updateError } = await supabase
          .from('client_profiles')
          .update(payload)
          .eq('id', profile.id)
          .select()
          .single();
        if (updateError) return { success: false, error: updateError.message };
        setProfile(data);
        const wasFirstSetup = !profile.setup_completed_at;
        if (wasFirstSetup) notifySetupComplete(data);
        return { success: true, data };
      }

      const { data, error: insertError } = await supabase
        .from('client_profiles')
        .insert([{ ...payload, user_id: user.id }])
        .select()
        .single();
      if (insertError) return { success: false, error: insertError.message };
      setProfile(data);
      notifySetupComplete(data);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const isSetupComplete = !!profile?.setup_completed_at;

  const value = {
    profile,
    loading,
    error,
    loadProfile,
    saveProfile,
    isSetupComplete,
    emptyProfile,
  };

  return (
    <ClientProfileContext.Provider value={value}>
      {children}
    </ClientProfileContext.Provider>
  );
};
